import CommentListing from "components/CommentListing/CommentListing";
import React, { useEffect, useState } from "react";
import { PropertyToEdit } from "../../../../redux/reducers/Properties/PropertiesSliceTypes";
import { getReviews } from "../../../../redux/reducers/Properties/PropertiesSlice";
import { AppDispatch } from "redux/store";
import { useDispatch } from "react-redux";
import { Star } from "lucide-react";

import { ENDPOINTS } from "API/apiConstants";
import API from "utils/helpers/APIHelper";

interface ReviewProps {
  propertyData: PropertyToEdit | null;
}

type SortKey = "recent" | "highest" | "lowest" | "oldest";
type RatingFilter = "all" | "five" | "fourBelow";

// How many reviews show first, and how many each "Show more" click adds.
const INITIAL_VISIBLE = 3;
const LOAD_STEP = 5;
// Sorting and filtering only help once there are a few reviews to sort.
const MIN_REVIEWS_FOR_CONTROLS = 4;

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "recent", label: "Most recent" },
  { value: "highest", label: "Highest rated" },
  { value: "lowest", label: "Lowest rated" },
  { value: "oldest", label: "Oldest first" },
];

const FILTER_OPTIONS: { value: RatingFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "five", label: "5 stars" },
  { value: "fourBelow", label: "4 stars and below" },
];

// Both legacy and new-platform reviews carry their score in `average`
// (CommentListing draws its stars from it).
const getRating = (review: any): number =>
  Number(review?.average ?? review?.rating ?? 0) || 0;

// The number of stars the card actually shows (4.5 draws 5 stars).
const getStars = (review: any): number => Math.ceil(getRating(review));

const getTime = (review: any): number => {
  const time = new Date(review?.created_at).getTime();
  return Number.isNaN(time) ? 0 : time;
};

// Shape legacy review into CommentListing's expected format
const normalizeLegacyReview = (legacyReview: any, index: number) => ({
  id: `legacy-${index}`,
  comment: legacyReview.content,
  rating: legacyReview.rating,
  average: legacyReview.rating || 0,
  created_at: legacyReview.date_raw,
  user: {
    name: legacyReview.author,
    image: null,
  },
});

const chipClass = (active: boolean) =>
  `min-h-[40px] px-4 py-1.5 rounded-full text-sm border transition ${
    active
      ? "bg-neutral-900 text-white border-neutral-900 dark:bg-white dark:text-neutral-900 dark:border-white"
      : "border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
  }`;

const outlineButtonClass =
  "w-full sm:w-auto min-h-[48px] px-6 rounded-xl border border-neutral-900 dark:border-neutral-200 bg-white dark:bg-neutral-900 text-sm font-semibold text-neutral-900 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition";

const Review: React.FC<ReviewProps> = ({ propertyData }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [showReview, setShowReview] = useState(false);
  const [reviews, setReviews] = useState<any[]>([]);
  const [legacyReviews, setLegacyReviews] = useState<any[]>([]);
  const [legacyAvgRating, setLegacyAvgRating] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("recent");
  const [ratingFilter, setRatingFilter] = useState<RatingFilter>("all");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const review = propertyData?.average?.average_review;
  const formattedReview = review ? review.toFixed(1) : null;

  // Merge and sort all reviews by date descending
  const allReviews = [...reviews, ...legacyReviews].sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const reviewCount = allReviews.length;

  // Unified average: combine new platform average with legacy average
  const unifiedAverage = (() => {
    const newCount = propertyData?.average?.count ?? 0;
    const newAvg = propertyData?.average?.average_review ?? 0;
    const legacyCount = legacyReviews.length;

    if (newCount > 0 && legacyCount > 0 && legacyAvgRating !== null) {
      return ((newAvg * newCount) + (legacyAvgRating * legacyCount)) / (newCount + legacyCount);
    }
    if (newCount > 0) return newAvg;
    if (legacyCount > 0) return legacyAvgRating;
    return null;
  })();

  const displayAverage = unifiedAverage ? unifiedAverage.toFixed(1) : formattedReview;

  const extractPropertyKey = (title: string): string => {
    const stopWords = /\d|bedroom|bathroom|apartment|house|studio|room|ocean|view|floor|story|suite|flat|penthouse/i;
    const words = title.trim().split(/\s+/);
    const result: string[] = [];

    for (const word of words) {
      if (stopWords.test(word)) break;
      result.push(word);
    }

    // Return at least 2 words, max 3
    return result.slice(0, 3).join(" ") || title;
  };

  const getReviewData = async () => {
    if (!propertyData) return;
    const propertyId = propertyData.id;
    //const propertyTitle = propertyData.title;
    const propertyTitle = extractPropertyKey(propertyData.title);
    console.log("🔍 Fetching reviews for:", propertyTitle, propertyId);

    setShowReview(true);

    try {
      const [newReviewsResult, legacyResult] = await Promise.allSettled([
        dispatch(getReviews(propertyId)).unwrap(),
        API.get(ENDPOINTS.LEGACY_REVIEWS(propertyTitle)),
      ]);

      console.log("📦 New reviews result:", newReviewsResult);
      console.log("📦 Legacy result:", legacyResult);

      if (newReviewsResult.status === "fulfilled") {
        console.log("✅ New reviews:", newReviewsResult.value);
        setReviews(newReviewsResult.value?.reviews || []);
      } else {
        console.error("❌ New reviews failed:", newReviewsResult.reason);
      }

      if (legacyResult.status === "fulfilled") {
        console.log("✅ Legacy raw response:", legacyResult.value);
        const legacyData = legacyResult.value?.data;
        console.log("✅ Legacy data:", legacyData);
        const normalized = (legacyData?.legacy_reviews || []).map(normalizeLegacyReview);
        console.log("✅ Normalized:", normalized);
        setLegacyReviews(normalized);
        setLegacyAvgRating(legacyData?.average_rating ?? null);

        console.log("PATH CHECK:", legacyResult.value?.data?.data?.legacy_reviews);
        console.log("PATH CHECK 2:", legacyResult.value?.data?.legacy_reviews);
      } else {
        console.error("❌ Legacy reviews failed:", legacyResult.reason);
      }
    } catch (error: any) {
      console.error("💥 Error fetching reviews:", error);
    }

    // Set AFTER all state updates, not in finally
    setShowReview(false);
  };

  useEffect(() => {
    getReviewData();
  }, []);

  const hasReviews = reviewCount > 0;
  const hasRating = (propertyData?.average?.count ?? 0) > 0 || legacyAvgRating !== null;

  console.log("🎨 Render state:", { hasReviews, hasRating, showReview, legacyReviews, legacyAvgRating, allReviews });

  if (!showReview && !hasReviews && !hasRating && legacyReviews.length === 0) {
    return null;
  }

  // ---- Sort, filter and "Show more" ----
  const filteredReviews = allReviews.filter((item) => {
    const stars = getStars(item);
    if (ratingFilter === "five") return stars >= 5;
    if (ratingFilter === "fourBelow") return stars < 5;
    return true;
  });

  const sortedReviews = [...filteredReviews].sort((a, b) => {
    switch (sortKey) {
      case "highest":
        return getRating(b) - getRating(a) || getTime(b) - getTime(a);
      case "lowest":
        return getRating(a) - getRating(b) || getTime(b) - getTime(a);
      case "oldest":
        return getTime(a) - getTime(b);
      default:
        return getTime(b) - getTime(a);
    }
  });

  const visibleReviews = sortedReviews.slice(0, visibleCount);
  const remainingCount = sortedReviews.length - visibleReviews.length;
  const nextBatch = Math.min(LOAD_STEP, remainingCount);
  const showControls = reviewCount >= MIN_REVIEWS_FOR_CONTROLS;

  const handleSortChange = (value: SortKey) => {
    setSortKey(value);
    setVisibleCount(INITIAL_VISIBLE);
  };

  const handleFilterChange = (value: RatingFilter) => {
    setRatingFilter(value);
    setVisibleCount(INITIAL_VISIBLE);
  };

  const reviewCategories = [
    {
      label: "Accuracy",
      rating: propertyData?.average?.accuracy || 0,
      icon: "📍",
    },
    {
      label: "Communication",
      rating: propertyData?.average?.communication || 0,
      icon: "💬",
    },
    {
      label: "Cleanliness",
      rating: propertyData?.average?.cleanliness || 0,
      icon: "✨",
    },
    {
      label: "Location",
      rating: propertyData?.average?.location || 0,
      icon: "📍",
    },
    {
      label: "Check In",
      rating: propertyData?.average?.check_in || 0,
      icon: "🔑",
    },
    {
      label: "Value",
      rating: propertyData?.average?.values || 0,
      icon: "💎",
    },
  ];

  return (
    <div className="listingSection__wrap w-full">
      {/* Header Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl">
              <Star size={24} className="text-yellow-500 fill-yellow-500" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Guest reviews
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {reviewCount > 0
                  ? `${reviewCount} ${reviewCount === 1 ? "review" : "reviews"} from verified guests`
                  : "No reviews yet"}
              </p>
            </div>
          </div>

          {/* Overall Rating Badge */}
          {displayAverage && (
            <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
              <Star size={20} className="text-yellow-500 fill-yellow-500" />
              <span className="text-2xl font-bold text-gray-900 dark:text-white">
                {displayAverage}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                / 5.0
              </span>
            </div>
          )}
        </div>

        <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent" />

        {/* Rating Categories Grid - only show if new platform has ratings */}
        {(propertyData?.average?.count ?? 0) > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {reviewCategories.map((category, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gradient-to-br from-white to-gray-50 dark:from-neutral-800 dark:to-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-700 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{category.icon}</span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {category.label}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Star size={16} className="text-yellow-500 fill-yellow-500" />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {category.rating.toFixed(1)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sort and filter */}
      {showControls && (
        <div className="mt-2 mb-4 flex w-full max-w-full min-w-0 flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 flex-wrap gap-2" role="group" aria-label="Filter reviews by rating">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={ratingFilter === option.value}
                onClick={() => handleFilterChange(option.value)}
                className={chipClass(ratingFilter === option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <label className="flex min-w-0 max-w-full items-center gap-2 md:flex-none">
            <span className="text-sm text-neutral-500 dark:text-neutral-400 whitespace-nowrap">
              Sort by
            </span>
            <select
              value={sortKey}
              onChange={(e) => handleSortChange(e.target.value as SortKey)}
              aria-label="Sort reviews"
              className="min-w-0 max-w-full flex-1 md:flex-none md:w-44 min-h-[44px] rounded-xl border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-base sm:text-sm font-medium text-neutral-900 dark:text-neutral-100 focus:outline-none focus:border-neutral-900 dark:focus:border-neutral-300"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {/* Reviews List */}
      {visibleReviews.length > 0 && (
        <div className="space-y-4">
          {visibleReviews.map((item) => (
            <div key={item.id}>
              <CommentListing data={item} />
            </div>
          ))}
        </div>
      )}

      {/* A filter that matches nothing */}
      {hasReviews && sortedReviews.length === 0 && (
        <div className="py-8 text-center">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            No reviews match this filter.
          </p>
          <button
            type="button"
            onClick={() => handleFilterChange("all")}
            className={`${outlineButtonClass} mt-3`}
          >
            Show all reviews
          </button>
        </div>
      )}

      {/* Show more / Show fewer */}
      {sortedReviews.length > INITIAL_VISIBLE && (
        <div className="mt-6 flex flex-col items-center gap-3">
          <p
            className="text-xs text-neutral-500 dark:text-neutral-400"
            aria-live="polite"
          >
            Showing {visibleReviews.length} of {sortedReviews.length}{" "}
            {sortedReviews.length === 1 ? "review" : "reviews"}
          </p>
          {remainingCount > 0 ? (
            <button
              type="button"
              onClick={() => setVisibleCount((count) => count + LOAD_STEP)}
              className={outlineButtonClass}
            >
              Show {nextBatch} more {nextBatch === 1 ? "review" : "reviews"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setVisibleCount(INITIAL_VISIBLE)}
              className={outlineButtonClass}
            >
              Show fewer reviews
            </button>
          )}
        </div>
      )}

      {/* Verified badge */}
      {reviewCount > 0 && (
        <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
          <p className="text-sm text-yellow-900 dark:text-yellow-100">
            <span className="font-semibold">Verified reviews:</span> All reviews
            are from guests who have completed a stay at this property.
          </p>
        </div>
      )}
    </div>
  );
};

export default Review;